import React, { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useFormContext } from 'react-hook-form';
import { useMst } from '../../../models/stores/Root';
import LabeledTextbox from '../../input/LabeledTextbox';
import PadLock from '../../content/PadLock';
import ToolTip from '../../content/ToolTip';
import LabeledDropdown from '../../input/LabeledDropdown';
import SpreedlyFramedInput from '../../input/SpreedlyFramedInput';
import { SpreedlyValues, UIConstants } from '../../../utils/constants';
import {
  SpreedlyNewPaymentMethodEventHandlers,
  SpreedlyOnValidationHandler,
} from '../../../types/Spreedly';
import UtilityService from '../../../services/UtilityService';

export const SpreedlyCreditCardTestId =
  'SpreedlyCreditCard-methods-payment-input';

export type SpreedlyCreditCardProps = {
  spreedlyEventHandlers: SpreedlyNewPaymentMethodEventHandlers;
};

const SpreedlyCreditCard: React.FC<SpreedlyCreditCardProps> = observer(
  ({ spreedlyEventHandlers }: SpreedlyCreditCardProps) => {
    const rootStore = useMst();
    const {
      register,
      formState: { errors },
    } = useFormContext<{
      creditCard: { cardHolderName: string; expMonth: string; expYear: string };
    }>();
    const [isRendered, setIsRendered] = useState(false);
    const [cardNumberErrorLabel, setCardNumberErrorLabel] = useState<
      string | undefined
    >(undefined);
    const [cvvErrorLabel, setCvvErrorLabel] = useState<string | undefined>(
      undefined
    );

    const handleOnValidationErrorLabels = (
      inputProperties: spreedly.SpreedlyFieldEventInputProperties,
      onValidationCallback: SpreedlyOnValidationHandler
    ) => {
      const {
        numberLength = 0,
        cvvLength = 0,
        validNumber,
        validCvv,
      } = inputProperties;
      setCardNumberErrorLabel(undefined);
      setCvvErrorLabel(undefined);
      if (numberLength < 14 || numberLength > 16 || !validNumber) {
        setCardNumberErrorLabel(UIConstants.INVALID_CARD_NUMBER);
      }
      if (!validCvv) {
        setCvvErrorLabel(UIConstants.INVALID_CVV);
      }
      if (cvvLength < 3 || cvvLength > 4) {
        setCvvErrorLabel(UIConstants.WRONG_LENGTH_CVV);
      }
      onValidationCallback(inputProperties);
    };

    const wrappedEventHandlers = useMemo(
      () => ({
        ...spreedlyEventHandlers,
        onValidation: (
          inputProperties: spreedly.SpreedlyFieldEventInputProperties
        ) =>
          handleOnValidationErrorLabels(
            inputProperties,
            spreedlyEventHandlers.onValidation
          ),
      }),
      [spreedlyEventHandlers]
    );

    useEffect(() => {
      if (!isRendered) {
        rootStore.initializeSpreedlyInputs(wrappedEventHandlers);
        setIsRendered(true);
      }
    }, [rootStore, wrappedEventHandlers, isRendered]);

    return (
      <div data-testid={SpreedlyCreditCardTestId}>
        <div className="flex flex-col">
          <SpreedlyFramedInput
            id={SpreedlyValues.CARD_NUMBER_ID}
            label="Card Number"
            icon={<PadLock />}
            errorLabel={cardNumberErrorLabel}
          />
        </div>
        <div className="flex flex-col">
          <LabeledTextbox
            name="creditCard.cardHolderName"
            label="Name on Card"
            disablePlaceholder
            register={register}
            errorLabel={errors?.creditCard?.cardHolderName?.message}
          />
        </div>
        <div className="flex flex-row w-full justify-between">
          <LabeledDropdown
            className="w-full"
            name="creditCard.expMonth"
            label="Month"
            options={UtilityService.getMonthListAsSelectOptions()}
            register={register}
            errorLabel={errors?.creditCard?.expMonth?.message}
          />
          <LabeledDropdown
            className="w-full"
            name="creditCard.expYear"
            label="Year"
            options={UtilityService.getYearListAsSelectOptions()}
            register={register}
            errorLabel={errors?.creditCard?.expYear?.message}
          />
          <SpreedlyFramedInput
            id={SpreedlyValues.CVV_ID}
            label="CVV"
            icon={
              <ToolTip
                tipText={UIConstants.CVV_TOOL_TIP}
                yPosition="above"
                xPosition="left"
                isInputIcon
              />
            }
            errorLabel={cvvErrorLabel}
          />
        </div>
      </div>
    );
  }
);

export default SpreedlyCreditCard;
